import { createContext, useContext, useState, ReactNode } from 'react';

/**
 * UIModeContext - Manages Simple/Advanced mode toggle
 *
 * Features:
 * - Global mode state (simple or advanced)
 * - Persists mode preference to localStorage
 * - Helper flags for conditional rendering
 */

export type UIMode = 'simple' | 'advanced';

interface UIModeContextValue {
  mode: UIMode;
  setMode: (mode: UIMode) => void;
  isSimple: boolean;
  isAdvanced: boolean;
}

const UIModeContext = createContext<UIModeContextValue | undefined>(undefined);

const STORAGE_KEY = 'emdr-ui-mode';

// Provider component
interface UIModeProviderProps {
  children: ReactNode;
}

export function UIModeProvider({ children }: UIModeProviderProps): JSX.Element {
  // Initialize from localStorage, default to 'simple'
  const [mode, setModeState] = useState<UIMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'simple' || stored === 'advanced') {
        return stored;
      }
    } catch (error) {
      console.warn('[UIModeContext] Failed to read from localStorage:', error);
    }
    return 'simple';
  });

  // Persist mode changes to localStorage
  const setMode = (newMode: UIMode): void => {
    setModeState(newMode);
    try {
      localStorage.setItem(STORAGE_KEY, newMode);
      console.log('[UIModeContext] Mode changed to:', newMode);
    } catch (error) {
      console.warn('[UIModeContext] Failed to write to localStorage:', error);
    }
  };

  // Helper flags for conditional rendering
  const isSimple = mode === 'simple';
  const isAdvanced = mode === 'advanced';

  const value: UIModeContextValue = {
    mode,
    setMode,
    isSimple,
    isAdvanced,
  };

  return (
    <UIModeContext.Provider value={value}>
      {children}
    </UIModeContext.Provider>
  );
}

// Custom hook to use UI mode context
export function useUIMode(): UIModeContextValue {
  const context = useContext(UIModeContext);
  if (context === undefined) {
    throw new Error('useUIMode must be used within a UIModeProvider');
  }
  return context;
}
